import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { Section } from "@/components/Section";
import { useTestimonials } from "@/hooks/useTestimonials";
import { useImageFallback } from "@/hooks/useImageFallback";
import { initialFor, isValidHttpsUrl, type TestimonialItem } from "@/lib/testimonials-defaults";

function metaFor(t: TestimonialItem): string {
  const parts: string[] = [];
  if (t.designation.trim()) parts.push(t.designation.trim());
  if (t.company.trim()) parts.push(t.company.trim());
  if (t.isDemo) parts.push("Demo Content");
  return parts.join(" · ");
}

function Avatar({ t }: { t: TestimonialItem }) {
  const url = isValidHttpsUrl(t.avatarUrl) ? t.avatarUrl : "";
  const { onError, failed } = useImageFallback("");
  if (!url || failed) {
    return (
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary font-display text-lg font-medium text-primary"
        aria-hidden
      >
        {initialFor(t.customerName)}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={t.altText ?? `${t.customerName} — testimonial`}
      loading="lazy"
      decoding="async"
      onError={onError}
      className="h-12 w-12 rounded-full object-cover bg-secondary"
    />
  );
}

export function TestimonialSlider() {
  const { testimonials, isEmpty, isLoading } = useTestimonials();
  const [i, setI] = useState(0);
  const count = testimonials.length;

  useEffect(() => {
    if (i >= count && count > 0) setI(0);
  }, [count, i]);

  if (isLoading || (isEmpty && count === 0)) {
    return (
      <Section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto mb-3 h-px w-10 bg-primary-foreground/40" aria-hidden />
          <span className="text-xs uppercase tracking-[0.2em] text-primary-foreground/80">
            Kind words
          </span>
          <div className="mx-auto mt-10 max-w-2xl rounded-3xl bg-card p-8 text-card-foreground shadow-xl ring-1 ring-border md:p-10">
            <p className="font-display text-lg text-foreground">
              {isLoading ? "Loading testimonials…" : "New reviews are on their way."}
            </p>
            {!isLoading ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Check back soon to hear from our community.
              </p>
            ) : null}
          </div>
        </div>
      </Section>
    );
  }

  const q = testimonials[Math.min(i, count - 1)];
  const stars = Math.max(1, Math.min(5, q.rating));
  const go = (n: number) => setI((i + n + count) % count);
  const meta = metaFor(q);

  return (
    <Section className="bg-primary text-primary-foreground">
      <div className="mx-auto max-w-3xl text-center">
        <div className="mx-auto mb-3 h-px w-10 bg-primary-foreground/40" aria-hidden />
        <span className="text-xs uppercase tracking-[0.2em] text-primary-foreground/80">Kind words</span>

        <div
          key={i}
          className="mx-auto mt-10 max-w-2xl animate-fade-in rounded-3xl bg-card p-8 text-left text-card-foreground shadow-xl ring-1 ring-border md:p-10"
        >
          <div
            className="flex items-center gap-1 text-accent"
            aria-label={`${stars} out of 5 stars`}
          >
            {Array.from({ length: stars }).map((_, s) => (
              <Star key={s} size={16} fill="currentColor" strokeWidth={0} aria-hidden />
            ))}
          </div>
          <Quote className="mt-6 text-primary/60" size={28} aria-hidden />
          <blockquote className="mt-4 font-display text-xl leading-snug text-foreground md:text-2xl">
            "{q.testimonial}"
          </blockquote>
          <div className="mt-8 flex items-center gap-4 border-t border-border pt-6">
            <Avatar t={q} />
            <div>
              <div className="text-sm font-medium text-foreground">{q.customerName}</div>
              {meta ? (
                <div className="text-xs italic text-muted-foreground">{meta}</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            onClick={() => go(-1)}
            type="button"
            aria-label="Previous testimonial"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/30 hover:bg-primary-foreground/10"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2" aria-label="Testimonial pagination">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setI(idx)}
                aria-label={`Go to testimonial ${idx + 1}`}
                aria-current={i === idx ? "true" : undefined}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-primary-foreground" : "w-1.5 bg-primary-foreground/40"}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            type="button"
            aria-label="Next testimonial"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/30 hover:bg-primary-foreground/10"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </Section>
  );
}